/* eslint-disable react/jsx-props-no-spreading */
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// Components
// @ts-ignore
import {
  Button, Textfield, Textarea, Formblock,
// @ts-ignore
} from '@zolteam/zol-admin-ui';
// Services
import { useCallback } from 'react';
import { postCreateSpecialCollection, putUpdateSpecialCollection } from
  '../../services/special-collections/specialCollectionsService';
// Validations
import formValidation from './validations/SpecialCollectionsForm.validation';

// Components
import AddressAutocomplete from '../../components/atoms/AddressAutocomplete/AddressAutocomplete';
import { useGetSpecialCollectionQuery }
  from '../../services/special-collections/specialCollectionsQueries';

// Utils
import { addHourToDate, combineDateTime } from '../../utils/date';
import FormSkeleton from '../cortizol/contents/ContentTypeForm/FormSkeleton';

const FORM_NAME = 'newSpecialCollection';
const TRAD_KEY = 'specialCollectionForm';

/** @type {SpecialCollection} */
const initValues = {
  name: '',
  startDate: null,
  endDate: null,
  startTime: '',
  endTime: '',
  description: '',
  publishedDate: null,
  place: '',
  address: {
    streetNumber: '',
    streetName: '',
    postalCode: '',
    city: '',
  },
  longitude: '',
  latitude: '',
};

/**
 * SpecialCollectionForm component.
 * @returns {JSX.Element} The rendered component.
 */
function SpecialCollectionForm() {
  // Hooks
  const { specialCollectionId } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();

  // API Calls
  const postCreateSpecialCollectionMutation = useMutation(postCreateSpecialCollection, {
    onSuccess: () => {
      navigate('/special-collections');
    },
  });

  const putUpdateSpecialCollectionMutation = useMutation(putUpdateSpecialCollection, {
    onSuccess: () => {
      navigate('/special-collections');
    },
  });

  const {
    isLoading: isLoadingSpecialCollection,
    data: specialCollectionData,
  } = useGetSpecialCollectionQuery(
    specialCollectionId,
    { enabled: specialCollectionId !== 'new' },
  );

  /**
   * Build the initial values for the form.
   * @param {SpecialCollection} data - The specialCollection data.
   * @returns {SpecialCollection} The initial values.
   */
  const buildEditValues = useCallback((/** @type {SpecialCollection} */ data) => {
    if (!data) return initValues;

    /**
     * @param {Address} place
     * @returns {string}
     */
    const placeString = (place) => {
      if (!place?.streetName) return '';
      // eslint-disable-next-line max-len
      return `${place.streetNumber || ''} ${place.streetName || ''}, ${place.postalCode || ''} ${place.city || ''}`;
    };

    /**
     * Normalize dates and times for the form.
     * @returns {object} The normalized date and time values.
     */
    const normalizeDates = () => ({
      startDate: combineDateTime(data.startDate, data.startTime),
      endDate: combineDateTime(data.endDate, data.endTime),
      publishedDate: addHourToDate(data.publishedDate, 2).toISOString().split('T')[0],
    });

    return {
      ...data,
      place: placeString(data.address),
      ...normalizeDates(),
    };
  }, [specialCollectionData, isLoadingSpecialCollection]);

  const {
    handleSubmit,
    formState,
    register,
    control,
    setValue,
    trigger,
  } = useForm({
    values: buildEditValues(specialCollectionData) || initValues,
    mode: 'onChange',
    resolver: yupResolver(formValidation),
  });

  const { errors } = formState;

  // Handlers
  /**
   * Extract specific details from a Google Place object.
   * @param {google.maps.places.Place} placeObject - The Google Place object.
   * @param {string} type - The type of address component to extract.
   * @returns {string} the adress detail
   */
  const getAddressComponent = (placeObject, type) => {
    if (!placeObject) return '';
    return placeObject.address_components
      ?.find((component) => component.types.includes(type))?.long_name || '';
  };
  /**
   * Handles the address change event.
   * @param {google.maps.places.Place} address - The address object.
   * @returns {void}
   */
  const handleAddressChange = (address) => {
    if (!address?.address_components) return;
    setValue('address', {
      streetNumber: getAddressComponent(address, 'street_number'),
      streetName: getAddressComponent(address, 'route'),
      postalCode: getAddressComponent(address, 'postal_code'),
      city: getAddressComponent(address, 'locality'),
    });
    setValue('place', address.formatted_address);
    setValue('latitude', `${address.geometry.location.lat()}`);
    setValue('longitude', `${address.geometry.location.lng()}`);
  };

  /**
   * Split a date-time string into date and time.
   * @param {string} isoString - The date-time string.
   * @returns {{ date: string, time: string }} The date and time.
   */
  const splitDateTime = (isoString) => {
    const date = isoString.split('T')[0];
    const time = isoString.split('T')[1];
    return { date, time };
  };

  /**
   * Handles the form submission to create a new specialCollection.
   * @param {any} formValues - The form values.
   * @returns {void}
   */
  const onSubmit = (formValues) => {
    const { date: startDate, time: startTime } = splitDateTime(
      new Date(formValues.startDate).toISOString().replace(/\.\d{3}Z$/, ''),
    );
    const { date: endDate, time: endTime } = splitDateTime(
      new Date(formValues.endDate).toISOString().replace(/\.\d{3}Z$/, ''),
    );
    const publishedDate = addHourToDate(formValues.publishedDate, 2)
      .toISOString().replace(/\.\d{3}Z$/, '').replace('T', ' ');
    const body = {
      name: formValues.name,
      address: formValues.address,
      startDate,
      endDate,
      startTime,
      endTime,
      description: formValues.description,
      longitude: formValues.longitude,
      latitude: formValues.latitude,
      publishedDate,
    };

    if (specialCollectionId !== 'new') {
      // @ts-ignore
      putUpdateSpecialCollectionMutation.mutate({ id: specialCollectionId, ...body });
    } else {
      postCreateSpecialCollectionMutation.mutate(body);
    }
  };

  return (
    <div className="padding-l">
      <h1 className="h1-default margin-bottom-m" style={{ fontWeight: 'bold' }}>
        {specialCollectionId === 'new'
          ? t('specialCollectionForm.title')
          : t('specialCollectionForm.titleEdit')}
      </h1>
      {isLoadingSpecialCollection && specialCollectionId !== 'new' ? <FormSkeleton /> : (
        <>
          <form
            id={FORM_NAME}
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(12, 1fr)',
              gap: '0.5rem',
            }}
            onSubmit={handleSubmit(onSubmit)}
          >

            <Formblock
              style={{ gridArea: '1 / 1 / 1 / 13' }}
              label={t(`${TRAD_KEY}.form.name`)}
              color={errors?.name?.message && 'error'}
              message={errors?.name?.message && t(errors?.name?.message)}
            >
              <Textfield
                label={t(`${TRAD_KEY}.form.name`)}
                placeholder={t(`${TRAD_KEY}.form.namePlaceholder`)}
                hasError={errors?.name?.message && t(errors?.name?.message)}
                {...register('name')}
              />
            </Formblock>

            <Formblock
              style={{ gridArea: '2 / 1 / 2 / 13' }}
              label={t(`${TRAD_KEY}.form.address`)}
              color={errors?.address?.streetName?.message && 'error'}
              message={errors?.address?.streetName?.message
            && t(errors?.address?.streetName?.message)}
            >
              <Controller
                name="place"
                control={control}
                render={({ field }) => (
                  <AddressAutocomplete
                    onChange={(address) => handleAddressChange(address)}
                    onChangeInput={field.onChange}
                    trigger={() => trigger('address')}
                  >
                    {(handleOnChange, handleBlur) => (
                      <Textfield
                        {...field}
                        label={t(`${TRAD_KEY}.form.address`)}
                        addressholder={t(`${TRAD_KEY}.form.address`)}
                        hasError={errors?.address?.streetName?.message
                          && t(errors?.address?.streetName?.message)}
                        onBlur={handleBlur}
                        onChange={handleOnChange}
                        autocomplete="off"
                      />
                    )}
                  </AddressAutocomplete>
                )}
              />
            </Formblock>

            <Formblock
              style={{ gridArea: '3 / 1 / 3 / 7' }}
              label={t(`${TRAD_KEY}.form.startDate`)}
              color={errors?.startDate?.message && 'error'}
              message={errors?.startDate?.message && t(errors?.startDate?.message)}
            >
              <Textfield
                type="datetime-local"
                label={t(`${TRAD_KEY}.form.startDate`)}
                placeholder={t(`${TRAD_KEY}.form.datePlaceholder`)}
                hasError={errors?.startDate?.message && t(errors?.startDate?.message)}
                {...register('startDate', {
                  setValueAs: (value) => {
                    if (value) {
                      const date = new Date(value);
                      date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
                      return date.toISOString().slice(0, 16);
                    }
                    return value;
                  },
                })}
              />
            </Formblock>

            <Formblock
              style={{ gridArea: '3 / 7 / 3 / 13' }}
              label={t(`${TRAD_KEY}.form.endDate`)}
              color={errors?.endDate?.message && 'error'}
              message={errors?.endDate?.message && t(errors?.endDate?.message)}
            >
              <Textfield
                type="datetime-local"
                label={t(`${TRAD_KEY}.form.endDate`)}
                placeholder={t(`${TRAD_KEY}.form.datePlaceholder`)}
                hasError={errors?.endDate?.message && t(errors?.endDate?.message)}
                {...register('endDate')}
              />
            </Formblock>

            {/* <Formblock
          style={{ gridArea: '6 / 1 / 6 / 7' }}
          label={t(`${TRAD_KEY}.form.startTime`)}
          color={errors?.startTime?.message && 'error'}
          message={errors?.startTime?.message && t(errors?.startTime?.message)}
        >
          <Textfield
            type="time"
            label={t(`${TRAD_KEY}.form.startTime`)}
            placeholder={t(`${TRAD_KEY}.form.timePlaceholder`)}
            hasError={errors?.startTime?.message && t(errors?.startTime?.message)}
            {...register('startTime')}
          />
        </Formblock>

        <Formblock
          style={{ gridArea: '6 / 7 / 6 / 13' }}
          label={t(`${TRAD_KEY}.form.endTime`)}
          color={errors?.endTime?.message && 'error'}
          message={errors?.endTime?.message && t(errors?.endTime?.message)}
        >
          <Textfield
            type="time"
            label={t(`${TRAD_KEY}.form.endTime`)}
            placeholder={t(`${TRAD_KEY}.form.timePlaceholder`)}
            hasError={errors?.endTime?.message && t(errors?.endTime?.message)}
            {...register('endTime')}
          />
        </Formblock> */}

            <Formblock
              style={{ gridArea: '4 / 1 / 4 / 13' }}
              label={t(`${TRAD_KEY}.form.description`)}
              color={errors?.description?.message && 'error'}
              message={errors?.description?.message && t(errors?.description?.message)}
            >
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <Textarea
                    {...field}
                    label={t(`${TRAD_KEY}.form.description`)}
                    placeholder={t(`${TRAD_KEY}.form.descriptionPlaceholder`)}
                    hasError={errors?.description?.message && t(errors?.description?.message)}
                  />
                )}
              />
            </Formblock>

            <Formblock
              style={{ gridArea: '5 / 1 / 5 / 7' }}
              label={t(`${TRAD_KEY}.form.publishedDate`)}
              color={errors?.publishedDate?.message && 'error'}
              message={errors?.publishedDate?.message && t(errors?.publishedDate?.message)}
            >
              <Textfield
                type="date"
                label={t(`${TRAD_KEY}.form.publishedDate`)}
                placeholder={t(`${TRAD_KEY}.form.publishedDatePlaceholder`)}
                hasError={errors?.publishedDate?.message && t(errors?.publishedDate?.message)}
                {...register('publishedDate')}
              />
            </Formblock>
          </form>

          <Button
            form={FORM_NAME}
            isSubmit
            isLoading={postCreateSpecialCollectionMutation.isLoading}
          >
            {t('specialCollectionForm.submit')}
          </Button>
        </>
      )}
    </div>
  );
}

export default SpecialCollectionForm;
